import type React from 'react';
import { Suspense, lazy } from 'react';
import { useFeedbackCollectorParams } from '@/hooks/use-feedback-collector-url';
import type { FeatureFeedbackCollectorProps } from '@/types/feedbackCollector';
import type { EvoMfeAppProps } from '@user-interfaces/evo-app-container';

const Noop: React.FC<EvoMfeAppProps> = () => null;

/**
 * Lazy-load EvoMfeApp so environments that do not provide
 * @user-interfaces/evo-app-container via an import map (e.g. Evo Test Drive)
 * degrade gracefully instead of crashing the entire app.
 */
const LazyEvoMfeApp = lazy(() =>
  import('@user-interfaces/evo-app-container')
    .then((mod) => ({ default: mod.EvoMfeApp }))
    .catch(() => ({ default: Noop }))
);

/**
 * A reusable feedback collector component that wraps the EvoMfeApp
 * micro-frontend host for the evo-feature-feedback-collector.
 *
 * This component automatically sources:
 * - userId from AuthProvider (authenticatedUser)
 * - customerName from CompanyContext (currentCompany)
 * - product, apiHost, and accountToken from configuration
 *
 * Only the `feature` prop needs to be specified per usage.
 *
 * Integration follows the Evo Application Integration guide:
 * https://compass.accessacloud.com/docs/Observability/pervasive-telemetry/feature-feedback/integration/evo-integration
 *
 * @example
 * ```tsx
 * <FeatureFeedbackCollector feature="Homepage" />
 * <FeatureFeedbackCollector feature="Cost Centres" />
 * ```
 */
export const FeatureFeedbackCollector: React.FC<FeatureFeedbackCollectorProps> = ({
  feature,
  className,
}) => {
  const params = useFeedbackCollectorParams(feature);

  if (!params) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <LazyEvoMfeApp
        componentName="evo-feature-feedback-collector"
        componentVersion="1"
        componentParams={{
          ...params,
          reDisplayAfterDays: 0, // Always show for testing
        }}
        className={className}
      />
    </Suspense>
  );
};

export default FeatureFeedbackCollector;
